import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Divider,
  Link,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { usePageMeta } from '../hooks/usePageMeta';
import { useToast } from '../hooks/useToast';

const Manifesto: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  usePageMeta('The Seriously Manifesto — Dating Done Differently', 'A dating app that actually wants you to find love. Voice-first, no paywalls, no tricks. Read why we built Seriously differently.');

  const handleShare = async () => {
    const url = window.location.origin + '/manifesto';
    const title = 'Seriously — Our Manifesto';
    const text = 'A dating app that actually wants you to find love. No paywalls, no tricks — just real connection:';

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      showToast('Link copied to clipboard!');
    }
  };

  return (
    <Box sx={{ backgroundColor: "background.default", minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ borderBottom: "1px solid", borderColor: "divider", backgroundColor: "background.default", position: "sticky", top: 0, zIndex: 50 }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "space-between", py: 1 }}>
            <Box
              onClick={() => navigate(-1)}
              sx={{ display: "flex", alignItems: "center", gap: 0.8, cursor: "pointer", "&:hover .back-text": { color: "primary.main" } }}
            >
              <ArrowBackIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              <FavoriteIcon sx={{ fontSize: 14, color: "primary.main" }} />
              <Typography
                className="back-text"
                sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: "0.8rem", fontWeight: 700, color: "text.primary", transition: "color 0.2s" }}
              >
                Seriously
              </Typography>
            </Box>
            <Box
              onClick={handleShare}
              sx={{
                display: "flex", alignItems: "center", gap: 0.4,
                px: 1, py: 0.4, cursor: "pointer", borderRadius: "3px",
                border: "1px solid", borderColor: "divider",
                color: "text.disabled", transition: "all 0.2s",
                "&:hover": { color: "primary.main", borderColor: "primary.main" },
              }}
            >
              <ShareIcon sx={{ fontSize: 12 }} />
              <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.6rem", fontWeight: 500 }}>Share</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
        {/* Title */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
          <Typography variant="caption" sx={{ color: "primary.main", mb: 2, display: "block" }}>
            Our manifesto
          </Typography>
          <Typography variant="h3" sx={{ mb: 2, fontSize: { xs: "1.5rem", sm: "1.8rem" } }}>
            A dating app that actually wants you to find love.
          </Typography>
          <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontStyle: "italic", fontSize: "0.9rem", color: "text.secondary", maxWidth: 440, mx: 'auto' }}>
            No paywalls. No tricks. No financial incentive to keep you single.
          </Typography>
        </Box>

        {/* Principles */}
        <Typography variant="caption" sx={{ color: "text.disabled", mb: 3, display: "block" }}>
          What we stand for
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, sm: 4 } }}>
          <Section title="Voice, not text.">
            Text is easy to fake. Voice is real. You hear warmth, humour, hesitation, confidence —
            everything that matters and nothing a chatbot can replicate. Conversations on Seriously
            happen through voice and photos, never text.
          </Section>

          <Section title="Five conversations. Not fifty.">
            We limit you to five active conversations at a time. Not because we want to sell you more —
            because focus makes you a better conversationalist. When you're not juggling a dozen chats,
            you're actually present. And when you're present, real connections happen.
          </Section>

          <Section title="Endings matter.">
            On most apps, conversations die in silence. On Seriously, when a conversation ends, both
            people leave a short farewell message and a private reflection. Not a rating — a moment of
            kindness.
          </Section>

          <Section title="No paywalls. No premium tiers. No tricks.">
            Seriously is free. Not freemium. Not "free with limitations." Free. There's no hidden "who
            liked you" page. No artificial scarcity. No features locked behind a subscription.
          </Section>

          <Section title="Your preferences, not our categories.">
            We don't ask you to check boxes about who you're attracted to. Instead, our matching learns
            from the faces and voices you respond to — so your experience reflects who you actually are,
            not who a form thinks you should be.
          </Section>

          <Section title="Fair attention for everyone.">
            Most dating apps create a winner-take-all dynamic. We designed Seriously to prevent this.
            Our algorithm actively balances visibility — everyone gets a fair chance at being seen, not
            just the people the algorithm thinks are "popular."
          </Section>

          <Section title="Privacy is non-negotiable.">
            We don't sell your data. We don't use dark patterns. Your information is yours. Full stop.
          </Section>
        </Box>

        <Divider sx={{ my: { xs: 4, sm: 5 } }} />

        {/* Who it's for */}
        <Box sx={{ mb: { xs: 4, sm: 5 } }}>
          <Typography variant="caption" sx={{ color: "text.disabled", mb: 2, display: "block" }}>
            Who it's for
          </Typography>
          <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontStyle: "italic", fontSize: "0.9rem", color: "text.secondary", lineHeight: 1.7 }}>
            People who are done with the cycle. You want one real connection with one
            real person, and you're willing to show up as yourself to find it.
          </Typography>
        </Box>

        {/* Industry critique */}
        <Box sx={{ py: 3, px: { xs: 2.5, sm: 3 }, backgroundColor: "secondary.main", borderLeft: "3px solid", borderColor: "primary.main", mb: { xs: 4, sm: 5 } }}>
          <Typography variant="caption" sx={{ color: "primary.main", mb: 2, display: "block" }}>
            Who we don't want to be
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Dating apps are designed to keep you single. They make money when you swipe, not when you
            meet. They gamify loneliness into an engagement loop. The industry calls this a "credit
            economy" — behavioural science repurposed to find the exact price point of your loneliness.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Seriously is working toward charity status — because a dating app whose purpose is to help
            people find love shouldn't have a financial incentive to keep them single.
          </Typography>
          <Link
            href="/the-economy-of-loneliness.html"
            sx={{ fontFamily: "'DM Sans'", fontSize: "0.8rem", color: "primary.main", fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Read the full essay →
          </Link>
        </Box>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5 } }}>
          <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: { xs: "1.1rem", sm: "1.25rem" }, color: "text.primary", mb: 1.5 }}>
            If that's you — welcome.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            Tell your friends.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/create-account')}
            >
              Create Account
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
              onClick={handleShare}
            >
              Share
            </Button>
          </Box>
        </Box>

        {/* Closing */}
        <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontStyle: 'italic', fontSize: "0.85rem", color: 'text.secondary', textAlign: 'center', mb: 3 }}>
          No BS. No gimmicks. Just real people, real intentions, and real chances to find your person.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center', pb: 2 }}>
          <Link component={RouterLink} to="/privacy" sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", color: "text.disabled", textDecoration: "none", mx: 1, "&:hover": { color: "primary.main" } }}>
            Privacy
          </Link>
          <Link component={RouterLink} to="/terms" sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", color: "text.disabled", textDecoration: "none", mx: 1, "&:hover": { color: "primary.main" } }}>
            Terms
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.9rem", fontWeight: 500, color: "text.primary", mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="body2">
      {children}
    </Typography>
  </Box>
);

export default Manifesto;
